import { CenterSpin } from '@/components/CenterSpin';
import { useTeamUsers } from '@/queries/team-users';
import { useTeamById } from '@/queries/teams';
import { useUsersByIds } from '@/queries/users';
import EditOutlined from '@ant-design/icons/EditOutlined';
import HomeOutlined from '@ant-design/icons/HomeOutlined';
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined';
import Breadcrumb from 'antd/es/breadcrumb';
import Button from 'antd/es/button';
import Card from 'antd/es/card';
import Divider from 'antd/es/divider';
import Input from 'antd/es/input';
import Space from 'antd/es/space';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { styled, useTheme } from 'styled-components';

const { Title, Text } = Typography;

const PageContainer = styled.div`
  ${({ theme }) => `
  padding: ${theme.paddingXL}px 0;
  max-width: ${theme.controlHeightLG * 20}px;
  margin: 0 auto;
`}
`;

const BreadcrumbContainer = styled.div`
  ${({ theme }) => `
  margin-bottom: ${theme.marginLG}px;
`}
`;

const Section = styled.div`
  ${({ theme }) => `
  margin-bottom: ${theme.marginXL}px;
`}
`;

const ProfileHeader = styled.div`
  ${({ theme }) => `
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.marginLG}px;
`}
`;

const ProfileInfo = styled.div`
  flex: 1;
`;

const EditableField = styled.div`
  ${({ theme }) => `
  display: flex;
  align-items: center;
  gap: ${theme.marginSM}px;
  margin-bottom: ${theme.margin}px;
  padding: ${theme.paddingXXS}px 0;

  .ant-input {
    max-width: 300px;
  }

  &:hover {
    background: ${theme.colorBgTextHover};
    border-radius: ${theme.borderRadiusSM}px;
  }
`}
`;

const MemberItem = styled.div`
  ${({ theme }) => `
  display: inline-block;
  padding: ${theme.paddingXS}px ${theme.paddingSM}px;
  border-radius: ${theme.borderRadiusXS * 3}px;
  transition: all ${theme.motionDurationMid};
  margin-bottom: ${theme.marginXS}px;

  &:hover {
    background: ${theme.colorBgTextHover};
  }
`}
`;

const MemberRole = styled(Text)<{ $isAdmin?: boolean }>`
  ${({ theme, $isAdmin }) => `
  font-size: ${theme.fontSizeSM}px;
  padding: ${theme.paddingXXS / 2}px ${theme.paddingXS}px;
  border-radius: ${theme.borderRadiusSM}px;
  background: ${$isAdmin ? theme.colorPrimaryBg : theme.colorBgTextHover};
  margin-left: ${theme.marginXS}px;
`}
`;

export const TeamSettingsPage = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { data: team, isLoading: isLoadingTeam } = useTeamById(teamId || null);
  const { data: teamUsers, isLoading: isLoadingTeamUsers } = useTeamUsers(
    { teamIds: teamId ? [teamId] : [] },
    { enabled: !!teamId },
  );
  const userIds = teamUsers?.items?.map((tu) => tu.userId) || [];
  const { data: users, isLoading: isLoadingUsers } = useUsersByIds(userIds, {
    enabled: userIds.length > 0,
  });
  const [editingField, setEditingField] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const theme = useTheme();

  const isLoading = isLoadingTeam || isLoadingTeamUsers || isLoadingUsers;

  const userMap = useMemo(() => {
    if (!users?.items) return new Map();
    return new Map(users.items.map((user) => [user.userId, user]));
  }, [users]);

  if (isLoading) return <CenterSpin />;
  if (!team) {
    return (
      <PageContainer>
        <Card>
          <Text type="danger">Failed to load team settings</Text>
        </Card>
      </PageContainer>
    );
  }

  const handleEditStart = (field: string) => {
    setEditingField(field);
    switch (field) {
      case 'name':
        setNewName(team.name);
        break;
    }
  };

  const handleUpdate = async (_field: string) => {
    if (!editingField) return;

    setIsUpdating(true);
    try {
      // TODO: implement actual API calls for each field
      setEditingField(null);
    } catch (error) {
      // handle error
    } finally {
      setIsUpdating(false);
    }
  };

  const renderEditableField = (
    field: string,
    label: string,
    value: string,
    placeholder?: string,
  ) => (
    <EditableField>
      <Text strong style={{ minWidth: `${theme.controlHeightLG * 2}px` }}>
        {label}
      </Text>
      {editingField === field ? (
        <>
          <Input
            value={newName}
            onChange={(e) => {
              switch (field) {
                case 'name':
                  setNewName(e.target.value);
                  break;
              }
            }}
            placeholder={placeholder}
            autoFocus
          />
          <Button
            type="primary"
            size="small"
            onClick={() => handleUpdate(field)}
            loading={isUpdating}
          >
            Save
          </Button>
          <Button size="small" onClick={() => setEditingField(null)}>
            Cancel
          </Button>
        </>
      ) : (
        <>
          <Text>{value}</Text>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditStart(field)}
            size="small"
          />
        </>
      )}
    </EditableField>
  );

  return (
    <PageContainer>
      <BreadcrumbContainer>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="/">
                  <HomeOutlined />
                </Link>
              ),
            },
            {
              title: 'Team Settings',
            },
          ]}
        />
      </BreadcrumbContainer>
      <Title
        level={4}
        style={{
          marginBottom: `${theme.marginLG}px`,
          textAlign: 'left',
          fontSize: `${theme.fontSizeLG}px`,
        }}
      >
        Team Settings
      </Title>
      <Card>
        <ProfileHeader>
          <ProfileInfo>
            {renderEditableField('name', 'Name', team.name, 'Enter team name')}
            <Text
              type="secondary"
              style={{
                fontSize: `${theme.fontSize}px`,
                display: 'block',
                marginBottom: `${theme.marginXS}px`,
              }}
            >
              Last updated {new Date(team.updatedAt).toLocaleDateString()}
            </Text>
            <Text
              copyable
              style={{
                fontFamily: 'monospace',
                fontSize: `${theme.fontSize - 2}px`,
                display: 'block',
                color: theme.colorTextSecondary,
                background: theme.colorBgTextHover,
                padding: `${theme.paddingXXS}px ${theme.paddingXS}px`,
                borderRadius: `${theme.borderRadiusSM}px`,
                width: 'fit-content',
              }}
            >
              {team.teamId}
            </Text>
          </ProfileInfo>
        </ProfileHeader>

        <Divider style={{ margin: `${theme.marginLG}px 0` }} />

        <Section>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: `${theme.margin}px`,
            }}
          >
            <Title level={5} style={{ margin: 0 }}>
              Members
            </Title>
            <Tooltip title="Hover over member names to see user IDs">
              <InfoCircleOutlined
                style={{ color: theme.colorTextSecondary, cursor: 'help' }}
              />
            </Tooltip>
          </div>
          <Space direction="vertical" style={{ width: '100%' }}>
            {teamUsers?.items?.map((tu) => {
              const user = userMap.get(tu.userId);
              return (
                <Tooltip key={tu.userId} title={`User ID: ${tu.userId}`}>
                  <MemberItem>
                    <Text style={{ fontSize: `${theme.fontSize}px` }}>
                      {user?.fullName || tu.userId}
                    </Text>
                    <MemberRole $isAdmin={tu.role === 'admin'}>
                      {tu.role}
                    </MemberRole>
                  </MemberItem>
                </Tooltip>
              );
            })}
            {(!teamUsers?.items || teamUsers.items.length === 0) && (
              <Text type="secondary">No members</Text>
            )}
          </Space>
        </Section>
      </Card>
    </PageContainer>
  );
};
